import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { useEffect, useState } from 'react'
import { useRunSelfTest, useRerunEndpoint } from '../../../hooks/admin/useSelfTest'
import type { SelfTestReport } from '../../../types/selfTest'
import { SelfTestSummary } from './SelfTestSummary'
import { SelfTestResultsTable } from './SelfTestResultsTable'
import { CopyResultsButton } from './CopyResultsButton'

/**
 * The Endpoints tab.
 *
 * Runs only on an explicit click — never on mount. A health panel that fires
 * ~70 loopback requests every time somebody opens the page is a self-inflicted
 * load generator, and one that trains people to ignore whatever it shows.
 */
export function SelfTestSection() {
  const runSelfTest = useRunSelfTest()
  const rerunEndpoint = useRerunEndpoint()

  // Rows re-run individually are folded into a local copy of the report, so a
  // single re-check updates just its own row rather than replacing the sweep.
  const [patched, setPatched] = useState<SelfTestReport | null>(null)
  const [rerunningPath, setRerunningPath] = useState<string | null>(null)

  const report = patched ?? runSelfTest.data ?? null

  // A live counter beats a guess: the run is bounded by the API's 45s deadline,
  // but how long it actually takes depends on how busy the database is.
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    if (!runSelfTest.isPending) return
    setElapsed(0)
    const started = Date.now()
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000)
    return () => clearInterval(timer)
  }, [runSelfTest.isPending])

  const handleRun = (refresh: boolean) => {
    setPatched(null)
    runSelfTest.mutate(refresh)
  }

  const handleRerun = (path: string) => {
    if (!report) return
    setRerunningPath(path)
    rerunEndpoint.mutate(path, {
      onSuccess: (fresh) => {
        const updates = new Map(fresh.results.map((r) => [`${r.method} ${r.path}`, r]))
        const results = report.results.map((r) => updates.get(`${r.method} ${r.path}`) ?? r)
        setPatched({
          ...report,
          results,
          // Recount, or the header keeps claiming failures the re-run just cleared.
          totals: {
            ...report.totals,
            pass: results.filter((r) => r.outcome === 'pass').length,
            fail: results.filter((r) => r.outcome === 'fail').length,
            notCovered: results.filter((r) => r.outcome === 'not-covered').length,
          },
        })
      },
      onSettled: () => setRerunningPath(null),
    })
  }

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">API endpoint self-test</h2>
            <p className="mt-1 text-sm text-gray-600">
              Calls every read-only endpoint on this API instance with <code>limit=1</code>, using your
              session. Read-only (GET) requests only — nothing is created, sent or modified.
            </p>
          </div>

          <div className="flex flex-col items-end gap-2">
            <button
              onClick={() => handleRun(Boolean(report))}
              disabled={runSelfTest.isPending}
              className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {runSelfTest.isPending && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
              {runSelfTest.isPending
                ? `Running… ${elapsed}s`
                : report
                  ? 'Re-run'
                  : 'Run self-test'}
            </button>
            {runSelfTest.isPending && (
              <p className="text-xs text-gray-500">
                Usually under 30s. The API gives up at 45s, so this cannot run away.
              </p>
            )}
          </div>
        </div>

        {runSelfTest.isError && (
          <p className="mt-4 text-sm text-red-600">
            {(runSelfTest.error as any)?.response?.data?.error?.message ||
              (runSelfTest.error as any)?.message ||
              'The self-test could not be run.'}
          </p>
        )}
      </section>

      {!report && !runSelfTest.isPending && !runSelfTest.isError && (
        <p className="text-sm text-gray-500">
          No run yet. Use this after deploying a new API build to confirm every endpoint still answers
          correctly.
        </p>
      )}

      {report && (
        <>
          <SelfTestSummary report={report} />
          <SelfTestResultsTable
            report={report}
            onRerun={handleRerun}
            rerunningPath={rerunningPath}
          />
          <CopyResultsButton report={report} />
        </>
      )}
    </div>
  )
}
